import React, { useMemo, useCallback, Suspense } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from 'react-router-dom';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useCase } from '../../hooks/useCase';
import { useUpdateCase } from '../../hooks/useUpdateCase';
import { SimpleCaseForm } from '../../components/organisms/CaseForm';
import type { SimpleCaseFormData } from '../../components/organisms/CaseForm';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { toast } from '../../utils/toast';
import type { UpdateCaseDto, LayoutBlock } from '@lnr-v2/types';
import { LayoutTemplate, FileText, Copy, ExternalLink } from 'lucide-react';

// Lazy load LayoutBuilder - it's heavy with dnd-kit
const LayoutBuilder = React.lazy(() =>
  import('../../components/organisms/LayoutBuilder/LayoutBuilder').then(
    (mod) => ({ default: mod.LayoutBuilder })
  )
);

export const CaseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive tab directly from URL - no useState needed
  const activeTab = useMemo(
    () => (searchParams.get('tab') === 'layout' ? 'layout' : 'content'),
    [searchParams]
  );

  // Track if layout tab was ever visited (for lazy mounting)
  const [layoutMounted, setLayoutMounted] = React.useState(false);
  React.useEffect(() => {
    if (activeTab === 'layout' && !layoutMounted) {
      setLayoutMounted(true);
    }
  }, [activeTab, layoutMounted]);

  // Memoized tab change handler
  const handleTabChange = useCallback(
    (tab: 'content' | 'layout') => {
      setSearchParams(
        (prev) => {
          if (tab === 'layout') {
            prev.set('tab', 'layout');
          } else {
            prev.delete('tab');
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const {
    case: caseDetail,
    loading: loadingCase,
    error: loadError,
  } = useCase(id);
  const { updateCase, loading: updating } = useUpdateCase();

  // Helper function to convert empty strings to null for optional fields
  // IMPORTANT: Use null (not undefined) to actually clear the field in Prisma
  // undefined = skip update, null = set to NULL in database
  const sanitizeOptionalField = (value: string | undefined): string | null => {
    return value && value.trim() !== '' ? value : null;
  };

  const handleSubmit = async (data: SimpleCaseFormData) => {
    if (!caseDetail?.id) return;

    try {
      // Convert simplified form data to UpdateCaseDto with sanitized optional fields
      const updateDto: UpdateCaseDto = {
        title: data.title,
        slogan: sanitizeOptionalField(data.slogan),
        clientId: data.clientId,
        shortDescription: sanitizeOptionalField(data.shortDescription),
        descriptionEN: sanitizeOptionalField(data.descriptionEN),
        descriptionFR: sanitizeOptionalField(data.descriptionFR),
        featuredImage: sanitizeOptionalField(data.featuredImage),
        thumbnailImage: sanitizeOptionalField(data.mobileImage),
        awards: data.awards,
        termIds: data.termIds,
        tagIds: data.tagIds,
        isFeatured: data.isFeatured,
        sortOrder: data.sortOrder,
        status: data.status,
      };

      // Debug: Log the payload being sent
      console.log('[CaseEditPage] Update payload:', {
        id: caseDetail.id,
        featuredImage: data.featuredImage,
        sanitizedFeaturedImage: updateDto.featuredImage,
        fullPayload: updateDto,
      });

      const updatedCase = await updateCase(caseDetail.id, updateDto);

      // Show success toast with translation action
      toast.success('Case updated successfully', {
        duration: 5000,
        action: {
          label: 'Manage Translations',
          onClick: () =>
            navigate(`/admin/translations?filter=cases.${updatedCase.slug}`),
        },
      });
      // No refetch needed - useUpdateCase invalidates queries automatically
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update case';
      toast.error(errorMessage);
    }
  };

  const handleSaveLayout = async (layout: LayoutBlock[]) => {
    if (!caseDetail?.id) return;
    try {
      await updateCase(caseDetail.id, { layout });
      toast.success('Layout saved successfully');
      // No need for refetch() - useUpdateCase already invalidates queries on success
    } catch (err) {
      toast.error('Failed to save layout');
      throw err; // Re-throw to prevent draft deletion
    }
  };

  const handleCancel = () => {
    navigate('/admin/cases');
  };

  if (loadingCase) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  if (loadError || !caseDetail) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800">
              {loadError ? 'Error loading case' : 'Case not found'}
            </h3>
            <p className="mt-1 text-sm text-red-700">
              {loadError?.message || 'The requested case could not be found.'}
            </p>
            <Link
              to="/admin/cases"
              className="mt-2 inline-block text-sm font-medium text-red-800 hover:text-red-700"
            >
              ← Back to cases
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('content')}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === 'content'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <FileText
                className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === 'content' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
              `}
              />
              Content
            </button>
            <button
              onClick={() => handleTabChange('layout')}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === 'layout'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <LayoutTemplate
                className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === 'layout' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
              `}
              />
              Layout Builder
            </button>
          </nav>
        </div>

        {/* Preview URL */}
        {caseDetail.previewToken &&
          (() => {
            const SITE_URL =
              import.meta.env.VITE_PUBLIC_SITE_URL ?? 'https://lnr.agency';
            const previewUrl = `${SITE_URL}/cases/preview/${caseDetail.previewToken}`;
            return (
              <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <span className="text-sm font-medium text-amber-800 whitespace-nowrap">
                  Preview URL
                </span>
                <code className="flex-1 truncate text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
                  {previewUrl}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(previewUrl);
                    toast.success('Preview URL copied to clipboard');
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-200 transition-colors"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-200 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open
                </a>
              </div>
            );
          })()}

        {/* Content Tab - Always mounted */}
        <div style={{ display: activeTab === 'content' ? 'block' : 'none' }}>
          <SimpleCaseForm
            initialData={{
              title: caseDetail.name,
              clientId: caseDetail.client?.id || '',
              slogan: caseDetail.slogan,
              shortDescription: caseDetail.shortDescription,
              descriptionEN: caseDetail.descriptionEN,
              descriptionFR: caseDetail.descriptionFR,
              featuredImage: caseDetail.featuredImage || caseDetail.cover,
              mobileImage: caseDetail.thumbnailImage,
              awards: caseDetail.awards || [],
              termIds: caseDetail.terms?.map((term) => term.id) || [],
              tagIds: caseDetail.tagIds || [],
              isFeatured: caseDetail.isFeatured,
              sortOrder: caseDetail.sortOrder || caseDetail.displayOrder || 1,
              status: caseDetail.status,
              slug: caseDetail.slug,
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={updating}
          />
        </div>

        {/* Layout Tab - Lazy loaded, mounted once visited, then hidden */}
        {layoutMounted && (
          <div
            className="h-[calc(100vh-200px)]"
            style={{ display: activeTab === 'layout' ? 'block' : 'none' }}
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-64">
                  <LoadingOverlay isLoading={true} />
                </div>
              }
            >
              <LayoutBuilder
                caseId={id || ''}
                initialLayout={caseDetail.layout || []}
                onSave={handleSaveLayout}
                loading={updating}
                caseDetail={caseDetail}
              />
            </Suspense>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
